import React, { useState } from "react";
import { Phone, MapPin, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, GURUKULA } from "../../lib/api";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/inquiries", form);
            toast.success("Pranam! Your message has reached the Gurukula. We will reach you soon.");
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="section-pad bg-cream"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-14">
                    <div className="eyebrow">· Visit · Write · Call ·</div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-maroon mt-4">
                        Begin your journey with us.
                    </h2>
                    <p className="max-w-2xl mx-auto mt-5 font-serif text-lg text-ink/80">
                        We welcome students of all ages — beginners to advanced — in both
                        Hindustani Classical Music and Sugama Sangeetha (Light Music).
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5 space-y-6">
                        <a
                            href={`tel:${GURUKULA.phone}`}
                            data-testid="contact-phone"
                            className="card-cream p-7 flex items-center gap-5 group"
                        >
                            <div className="w-14 h-14 flex items-center justify-center bg-maroon text-gold-300">
                                <Phone size={22} />
                            </div>
                            <div>
                                <div className="eyebrow text-gold">Phone</div>
                                <div className="font-sans font-medium text-3xl lg:text-4xl tabular-nums tracking-[0.1em] text-maroon group-hover:text-gold transition-colors mt-1">
                                    {GURUKULA.phone}
                                </div>
                            </div>
                        </a>

                        <a
                            href={GURUKULA.mapUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="contact-address"
                            className="card-cream p-7 flex items-center gap-5 group"
                        >
                            <div className="w-12 h-12 flex items-center justify-center bg-maroon text-gold-300">
                                <MapPin size={18} />
                            </div>
                            <div>
                                <div className="eyebrow text-gold">Visit Us</div>
                                <div className="font-display text-xl text-maroon group-hover:text-gold transition-colors">
                                    {GURUKULA.address}
                                </div>
                            </div>
                        </a>

                        <div className="border border-gold-100 overflow-hidden h-72 bg-white">
                            <iframe
                                title="Gurukula location"
                                src={GURUKULA.mapEmbed}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>

                    <form
                        onSubmit={submit}
                        data-testid="contact-form"
                        className="lg:col-span-7 bg-white border border-gold-100 p-8 lg:p-10"
                    >
                        <div className="eyebrow text-gold mb-2">Enquire about classes</div>
                        <h3 className="font-display text-3xl text-maroon">Write to the Gurukula</h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
                            <div>
                                <label className="eyebrow text-ink/70">Your name</label>
                                <input
                                    data-testid="contact-name-input"
                                    required
                                    value={form.name}
                                    onChange={update("name")}
                                    className="mt-2 w-full bg-cream border border-gold-100 px-4 py-3 font-serif text-ink focus:outline-none focus:border-maroon"
                                    placeholder="e.g. Aaradhya Sharma"
                                />
                            </div>
                            <div>
                                <label className="eyebrow text-ink/70">Email</label>
                                <input
                                    data-testid="contact-email-input"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={update("email")}
                                    className="mt-2 w-full bg-cream border border-gold-100 px-4 py-3 font-serif text-ink focus:outline-none focus:border-maroon"
                                    placeholder="name@email.com"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="eyebrow text-ink/70">Phone (optional)</label>
                                <input
                                    data-testid="contact-phone-input"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    className="mt-2 w-full bg-cream border border-gold-100 px-4 py-3 font-serif text-ink focus:outline-none focus:border-maroon"
                                    placeholder="+91 ..."
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="eyebrow text-ink/70">Your message</label>
                                <textarea
                                    data-testid="contact-message-input"
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={update("message")}
                                    className="mt-2 w-full bg-cream border border-gold-100 px-4 py-3 font-serif text-ink focus:outline-none focus:border-maroon"
                                    placeholder="I would like to learn Hindustani Classical Vocal…"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-button"
                            className="btn-primary mt-7 inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            <Send size={14} />
                            {submitting ? "Sending…" : "Send Enquiry"}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}
